import Tooltip from '@components/Tooltip';
import useStore from '@store/store';
import classNames from 'classnames';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { getChartColors } from 'utilities/helper';

const colors = getChartColors();

const radii = [34, 26, 18];

interface RadialBarChartType {
  question: string;
  region: string;
  scores: [number, number, number, number];
  max: number;
  index?: number;
  className?: string;
}

const RadialBarChart: React.FC<RadialBarChartType> = ({
  question = '',
  region = '',
  scores = [0, 0, 0, 0],
  max = 100,
  index,
  className = '',
}) => {
  const { ref, inView } = useInView({
    threshold: 1,
  });

  const { generatingPDF } = useStore();

  return (
    <div
      className={classNames(
        'group/tooltip rounded-full overflow-hidden cursor-pointer',
        className
      )}
      ref={ref}
    >
      <svg viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r="49.8"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.3"
          strokeWidth="0.4"
        />
        <circle
          className="transition-all duration-500 ease-out delay-500"
          transform="rotate(-90 50 50)"
          cx="50"
          cy="50"
          r="24"
          fill="none"
          stroke={colors[0]}
          strokeWidth="48"
          pathLength="1"
          strokeDasharray={`${generatingPDF || inView ? scores[0] / max || 0 : 0} 1`}
        />

        {radii.map((radius) => (
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="0.4"
            strokeDasharray="0.625"
            key={radius}
          />
        ))}

        <g transform="rotate(-90 50 50)">
          {radii.map((radius, index) => (
            <circle
              className="transition-all duration-500 ease-out"
              style={{
                transitionDelay: `${index * 250}ms`,
              }}
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={colors[index + 1]}
              strokeWidth="1.75"
              pathLength="1"
              strokeDasharray={`${generatingPDF || inView ? scores[index + 1] / max || 0 : 0} 1`}
              key={radius}
            />
          ))}
        </g>
      </svg>
      <Tooltip
        title={question}
        index={index}
        items={[
          {
            label: 'Your overall score',
            value: scores[0],
            color: colors[0],
          },
          {
            label: 'Global (average)',
            value: scores[1],
            color: colors[1],
          },
          {
            label: `Regional (average): ${region}`,
            value: scores[2],
            color: colors[2],
          },
          {
            label: 'Market leaders (average)',
            value: scores[3],
            color: colors[3],
          },
        ]}
      />
    </div>
  );
};

export default RadialBarChart;
